import { useRouteQuery } from '@/common/route/useRouteQuery';
import { pageSize } from '@/common/utils/helpers';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useAppStore } from '@/modules/apps/store';
import { usePromotionPrototypeList } from '@/modules/promotion/prototype/model/list';
import { ROUTE_NAME_PROMOTION } from '@/modules/promotion/router';
import { useRfmPersonaList } from '@/modules/rfm/persona/model/list';
import { promotionService } from '@/services/promotion';
import FileSaver from 'file-saver';
import { storeToRefs } from 'pinia';
import { watch } from 'vue';

export function usePromotionItemList(filter?: { type?: number; status?: number; rfmPersonaId?: number }) {
    const crudAction = useCrudActions();

    const { query, updateQueryToRoute } = useRouteQuery({
        definition: {
            type: { type: 'number', default: filter?.type || 0 },
            status: { type: 'number', default: filter?.status || -1 },
            rfmPersonaId: { type: 'number', default: filter?.rfmPersonaId || null },
            offset: { type: 'number', default: 0 },
            limit: { type: 'number', default: pageSize }
        },
        onChange: () => fetchList()
    });

    const { currentApp } = storeToRefs(useAppStore());
    const { personaOptions, fetchPersonaAll } = useRfmPersonaList();
    const { prototypeMap, fetchPrototypeAll } = usePromotionPrototypeList();

    const { list, total, fetchList } = useBaseListView({
        request: promotionService.item.list,
        paramsBuilder: () => ({ ...query }),
        hooks: {
            loadInitialData: async () => {
                await Promise.all([fetchPersonaAll(), fetchPrototypeAll()]);
            },
            onPageChange: offset => (query.offset = offset),
            onSuccess: updateQueryToRoute,
            transformResponse: (list: any[]) =>
                list.map(item => {
                    const prototype = prototypeMap.value.get(item.usedPrototypeId);
                    return {
                        ...item,
                        prototypeLink: prototype
                            ? { name: ROUTE_NAME_PROMOTION.PROTOTYPE.DETAIL, params: { id: item.usedPrototypeId } }
                            : '',
                        prototypeName: prototype ? `${item.usedPrototypeId} - ${prototype.name}` : item.usedPrototypeId
                    };
                })
        }
    });

    const actions = {
        remove: crudAction.confirmAndRemove({
            request: promotionService.item.remove,
            onSuccess: () => fetchList()
        }),
        disable: crudAction.confirmAndDisable({
            request: promotionService.item.disable,
            onSuccess: () => fetchList()
        }),
        enable: crudAction.confirmAndEnable({
            request: promotionService.item.enable,
            onSuccess: () => fetchList()
        }),
        export: crudAction.executeAction({
            request: () => promotionService.item.export(),
            onSuccess: ({ data }) => {
                const blob = new Blob([data], { type: 'text/csv;charset=utf-8' });
                const fileName = currentApp.value?.name + 'Promotion.csv';
                FileSaver.saveAs(blob, fileName);
            }
        })
    };

    // clear filter when type changed
    watch(
        () => query.type,
        () => {
            query.status = -1;
            query.rfmPersonaId = null;
        }
    );

    return { list, total, fetchList, actions, query, personaOptions };
}
